import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Media from '../types/Media';

const HeaderWrapper = styled.div`
  background: black;
  color: white;
  padding: 0.6em 1em;
  display: flex;
  align-items: center;

  @media (max-width: 360px) {
    padding: 0.6em 0.6em;
  }
`;

// Optional leading slot, used for a rank chip on ranked lists. Sits before the
// text so rank reads left-to-right; the play button keeps the trailing slot.
const LeftSlot = styled.div`
  margin-right: 0.6em;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const PlaySlot = styled.div`
  margin-left: 0.5em;
  font-size: 1.4em;
  width: 1.6em;
  height: 1.6em;
  border: 2px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Artist = styled.span`
  font-weight: 900;
`;

const ArtistLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 900;

  &:hover {
    text-decoration: underline;
  }
`;

const Title = styled.span`
  font-weight: 300;
  font-style: italic;
`;

const TitleLink = styled(Link)`
  color: white;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const Album = styled.span`
  font-weight: 300;
`;

const AlbumLink = styled(Link)`
  color: white;
  text-decoration: none;
  font-weight: 300;

  &:hover {
    text-decoration: underline;
  }
`;

const Year = styled.span`
  font-weight: 300;
  opacity: 0.6;
`;

// Flip to true to enable artist/song/album page links
var ENABLE_MUSIC_PAGES = true;

interface IMediaHeaderProps {
  media: Media;
  playButton?: React.ReactNode;
  leftSlot?: React.ReactNode;
}

// The bar's pieces, for a header in the same style that is not one song's
// (a 3x3's grid slide).
export {
  HeaderWrapper as MediaHeaderBar,
  LeftSlot as MediaHeaderLeftSlot,
  HeaderText as MediaHeaderText,
  PlaySlot as MediaHeaderPlaySlot,
};

export function MediaHeader(prop: IMediaHeaderProps): JSX.Element {
  const artist = prop.media.artist;
  const isSong = prop.media.song_title != null && prop.media.song_title !== '';
  const title = isSong ? prop.media.song_title : prop.media.album_title;
  const year = prop.media.release_date ? prop.media.release_date.slice(0, 4) : null;
  return (
    <HeaderWrapper>
      {prop.leftSlot && <LeftSlot>{prop.leftSlot}</LeftSlot>}
      <HeaderText>
        {ENABLE_MUSIC_PAGES ? (
          <ArtistLink
            to={
              '/artist/' +
              encodeURIComponent(artist) +
              (prop.media.apple_artist_id
                ? '?appleId=' + encodeURIComponent(prop.media.apple_artist_id)
                : '')
            }
            state={{ from: 'feed', fromPath: '/' }}
          >
            {artist}
          </ArtistLink>
        ) : (
          <Artist>{artist}</Artist>
        )}
        <br />
        {ENABLE_MUSIC_PAGES && prop.media.id ? (
          <TitleLink to={'/music/' + prop.media.id} state={{ from: 'feed', fromPath: '/' }}>
            <Title>{title}</Title>
          </TitleLink>
        ) : (
          <Title>{title}</Title>
        )}
        {isSong && prop.media.album_title ? (
          <>
            <br />
            {ENABLE_MUSIC_PAGES ? (
              <AlbumLink
                to={
                  '/music/album?artist=' +
                  encodeURIComponent(prop.media.artist) +
                  '&album=' +
                  encodeURIComponent(prop.media.album_title) +
                  (prop.media.upc ? '&upc=' + encodeURIComponent(prop.media.upc) : '')
                }
                state={{ from: 'feed', fromPath: '/' }}
              >
                {prop.media.album_title}
              </AlbumLink>
            ) : (
              <Album>{prop.media.album_title}</Album>
            )}
            {year && <Year> ({year})</Year>}
          </>
        ) : (
          year && <Year> ({year})</Year>
        )}
      </HeaderText>
      {prop.playButton && <PlaySlot>{prop.playButton}</PlaySlot>}
    </HeaderWrapper>
  );
}
